// Vigencia de una política o promoción (mock `.cal-row`): toggle Permanente (∞) + calendarios
// Desde/Hasta. Permanente = sin fecha de término; al apagarlo propone una ventana desde el inicio.
import { useState } from 'react';
import { cn } from '@/lib/cn';
import { MiniCalendar, firstOfMonth, isoOf } from './MiniCalendar';

function addDays(iso: string, days: number): string {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    return isoOf(d);
}

function shiftMonth(month: Date, dir: -1 | 1): Date {
    return new Date(month.getFullYear(), month.getMonth() + dir, 1);
}

export function ValidityCalendars({
    validFrom,
    validTo,
    today,
    disabled = false,
    onChange,
    onTogglePermanent,
}: {
    validFrom: string | null;
    validTo: string | null;
    today: string;
    disabled?: boolean;
    onChange: (validity: { validFrom: string | null; validTo: string | null }) => void;
    /** Ventana que estrena el dominio al apagar Permanente; sin él son 30 días desde el inicio. */
    onTogglePermanent?: (isPermanent: boolean) => void;
}) {
    const isPermanent = !validTo;
    const [fromMonth, setFromMonth] = useState(() => firstOfMonth(validFrom ?? today));
    const [toMonth, setToMonth] = useState(() => firstOfMonth(validTo ?? validFrom ?? today));

    // Al cambiar la vigencia desde fuera, cada calendario vuelve al mes de su fecha: sin esto la
    // tarjeta sigue mostrando el mes viejo y el día elegido queda fuera de la vista.
    const [seenValidity, setSeenValidity] = useState({ validFrom, validTo });
    if (seenValidity.validFrom !== validFrom || seenValidity.validTo !== validTo) {
        setSeenValidity({ validFrom, validTo });
        setFromMonth(firstOfMonth(validFrom ?? today));
        setToMonth(firstOfMonth(validTo ?? validFrom ?? today));
    }

    const pickFrom = (iso: string): void => {
        const next = { validFrom: iso, validTo };
        if (validTo && iso > validTo) next.validTo = iso;
        onChange(next);
    };
    const pickTo = (iso: string): void => {
        if (!validFrom || iso >= validFrom) onChange({ validFrom, validTo: iso });
    };
    const togglePermanent = (): void => {
        if (onTogglePermanent) {
            onTogglePermanent(isPermanent);
            return;
        }
        if (isPermanent) {
            onChange({ validFrom, validTo: addDays(validFrom ?? today, 30) });
        } else {
            onChange({ validFrom, validTo: null });
        }
    };

    return (
        <div className="flex min-w-0 items-stretch gap-1.5">
            <button
                type="button"
                aria-pressed={isPermanent}
                disabled={disabled}
                onClick={togglePermanent}
                className={cn(
                    'flex min-w-[56px] flex-none flex-col items-center justify-center gap-0.5 rounded-md border px-2 py-1 transition-colors disabled:opacity-60',
                    isPermanent
                        ? 'border-success-soft-border bg-success-soft text-success'
                        : 'border-border bg-card hover:bg-accent/40',
                )}
            >
                <span className="text-base leading-none">∞</span>
                <span className="text-[10px] font-semibold uppercase tracking-wide">
                    Permanente
                </span>
            </button>

            <div className="min-w-0 flex-1" role="group" aria-label="Desde">
                <div className="mb-0.5 text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
                    Desde
                </div>
                <MiniCalendar
                    month={fromMonth}
                    from={validFrom}
                    to={validTo}
                    today={today}
                    disabled={disabled}
                    onNavigate={(dir) => {
                        setFromMonth((m) => shiftMonth(m, dir));
                    }}
                    onPick={pickFrom}
                />
            </div>
            <div className="flex flex-none items-center px-0.5 text-xs text-muted-foreground">
                →
            </div>
            <div className="min-w-0 flex-1" role="group" aria-label="Hasta">
                <div className="mb-0.5 text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
                    Hasta
                </div>
                <MiniCalendar
                    month={toMonth}
                    from={validFrom}
                    to={validTo}
                    today={today}
                    disabled={disabled || isPermanent}
                    onNavigate={(dir) => {
                        setToMonth((m) => shiftMonth(m, dir));
                    }}
                    onPick={pickTo}
                />
            </div>
        </div>
    );
}
